<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="680px"
    overlay-opacity="0.94"
    content-class="glass-dialog"
  >
    <v-card class="modern-card" elevation="0">
      <!-- Header with animated close button -->
      <div class="dialog-header">
        <h2 class="dialog-title">{{ dialogTitle }}</h2>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          x-large
          depressed
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Main content -->
      <v-card-text class="dialog-content">
        <v-form ref="form" v-if="action !== 'delete'">
          <!-- Title Input -->
          <div class="form-section">
            <div class="section-title">Title</div>
            <v-text-field
              v-model="title"
              label="Title"
              outlined
              rounded
              dense
              color="accent"
              hide-details="auto"
              class="modern-input"
            >
              <template v-slot:prepend-inner>
                <v-icon small color="accent">mdi-format-title</v-icon>
              </template>
            </v-text-field>
          </div>

          <!-- Score Range -->
          <div class="score-range-section">
            <div class="section-title">Score Range</div>
            <div class="d-flex align-center">
              <v-text-field
                v-model="score_from"
                label="From"
                type="number"
                outlined
                rounded
                dense
                color="accent"
                hide-details="auto"
                class="modern-input score-input"
              ></v-text-field>
              <span class="mx-4">to</span>
              <v-text-field
                v-model="score_to"
                label="To"
                type="number"
                outlined
                rounded
                dense
                color="accent"
                hide-details="auto"
                class="modern-input score-input"
              ></v-text-field>
            </div>
          </div>

          <!-- Color Picker -->
          <div class="form-section">
            <div class="section-title">Select Color</div>
            <v-color-picker
              v-model="selectedColor"
              mode="hexa"
              hide-canvas
              dot-size="25"
              swatches-max-height="200"
              class="modern-color-picker"
            ></v-color-picker>
            <div class="color-preview" :style="{ backgroundColor: selectedColor }"></div>
          </div>
        </v-form>

        <!-- Delete Confirmation -->
        <div v-if="action === 'delete'" class="delete-confirmation">
          <v-icon color="error" large class="mb-4">mdi-alert-circle-outline</v-icon>
          <div class="delete-message">
            Are you sure you want to delete the color <strong>{{ title }}</strong>?
          </div>
        </div>

        <!-- Error Message -->
        <transition name="slide-fade">
          <v-alert
            v-if="message"
            type="error"
            dense
            outlined
            class="error-alert mt-4"
          >
            {{ message }}
          </v-alert>
        </transition>
      </v-card-text>

      <!-- Action Buttons -->
      <v-card-actions class="dialog-actions">
        <v-btn
          text
          large
          @click="onClose"
          class="cancel-btn"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          large
          @click="handleAction"
          :loading="loading"
          depressed
          class="submit-btn"
          dark
        >
          <v-icon left>{{ actionIcon }}</v-icon>
          {{ actionButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";

export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    action: {
      type: String,
      required: true
    },
    colorData: {
      type: Object,
      default: () => ({})
    },
    onClose: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      message: null,
      loading: false,
      title: this.colorData?.title || "",
      score_from: this.colorData?.score_from || "",
      score_to: this.colorData?.score_to || "",
      selectedColor: this.colorData?.colour_code || "#6200ee"
    };
  },
  computed: {
    dialogTitle() {
      return this.action === "create"
        ? "Add Score Color"
        : this.action === "edit"
        ? "Edit Score Color"
        : "Delete Color";
    },
    actionButtonText() {
      return this.action === "create"
        ? "Create"
        : this.action === "edit"
        ? "Save"
        : "Delete";
    },
    actionIcon() {
      return this.action === "create"
        ? "mdi-plus"
        : this.action === "edit"
        ? "mdi-content-save"
        : "mdi-delete";
    }
  },
  methods: {
    ...mapActions({
      createColor: "colour/createColor",
      updateColor: "colour/editColor",
      deleteColor: "colour/deleteColor"
    }),
    handleAction() {
      if (this.action === "create") {
        this.createColour();
      } else if (this.action === "edit") {
        this.editColour();
      } else if (this.action === "delete") {
        this.deleteColour();
      }
    },
    async createColour() {
      if (!this.title || !this.score_from || !this.score_to || !this.selectedColor) {
        this.message = "Please fill all required fields";
        return;
      }
      
      this.loading = true;
      const params = {
        title: this.title,
        score_from: this.score_from,
        score_to: this.score_to,
        colour_code: this.selectedColor
      };
      try {
        await this.createColor(params);
        this.message = null;
        this.$emit("action-complete", { action: "create", data: params });
        this.onClose();
      } catch (error) {
        this.message = error.response?.data?.message || "Error creating color";
      } finally {
        this.loading = false;
      }
    },
    async editColour() {
      if (!this.title || !this.score_from || !this.score_to || !this.selectedColor) {
        this.message = "Please fill all required fields";
        return;
      }
      
      this.loading = true;
      const params = {
        _id: this.colorData?._id,
        title: this.title,
        score_from: this.score_from,
        score_to: this.score_to,
        colour_code: this.selectedColor
      };
      try {
        await this.updateColor(params);
        this.message = null;
        this.$emit("action-complete", { action: "edit", data: params });
        this.onClose();
      } catch (error) {
        this.message = error.response?.data?.message || "Error editing color";
      } finally {
        this.loading = false;
      }
    },
    async deleteColour() {
      this.loading = true;
      try {
        await this.deleteColor({ _id: this.colorData?._id });
        this.message = null;
        this.$emit("action-complete", {
          action: "delete",
          data: this.colorData
        });
        this.onClose();
      } catch (error) {
        this.message = error || "Error deleting color";
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    colorData(newValue) {
      this.title = newValue?.title || "";
      this.score_from = newValue?.score_from || "";
      this.score_to = newValue?.score_to || "";
      this.selectedColor = newValue?.colour_code || "#226085";
    },
    dialog(newValue) {
      if (newValue && this.action === "edit") {
        this.selectedColor = this.colorData?.colour_code || "#226085";
      }
    }
  }
};
</script>

<style>
/* Glass morphism effect */
.glass-dialog {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.modern-card {
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18) !important;
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  background: linear-gradient(135deg,#084468,#3b7ca1);
  color: white;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.5px;
  margin: 0;
}

.close-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
  border-radius: 50%;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.dialog-content {
  padding: 24px 32px;
}

.form-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
  margin-bottom: 8px;
}

.score-range-section {
  margin-bottom: 24px;
}

.score-input {
  max-width: 120px;
}

.modern-input .v-input__slot,
.modern-color-picker .v-input__slot {
  border-radius: 12px !important;
  transition: all 0.3s ease;
  background: rgba(245, 245, 245, 0.7) !important;
}

.modern-input .v-input__slot:hover,
.modern-color-picker .v-input__slot:hover {
  background: rgba(245, 245, 245, 0.9) !important;
}

.modern-input .v-input__slot fieldset,
.modern-color-picker .v-input__slot fieldset {
  border-width: 1px !important;
  border-color: rgba(0, 0, 0, 0.1) !important;
}

.modern-input .v-label {
  color: #666 !important;
  font-size: 0.9rem;
}

.modern-color-picker {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.color-preview {
  width: 100%;
  height: 40px;
  border-radius: 8px;
  margin-top: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.delete-confirmation {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 0;
}

.delete-message {
  font-size: 1.1rem;
  color: #333;
}

.error-alert {
  margin-top: 16px;
  border-radius: 8px !important;
  border-left: 4px solid #ff5252 !important;
}

.dialog-actions {
  padding: 16px 32px;
  background: rgba(245, 245, 245, 0.7);
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}

.cancel-btn {
  color: #666 !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
}

.cancel-btn:hover {
  color: #333 !important;
}

.submit-btn {
  background: linear-gradient(135deg,#084468,#3b7ca1) !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(98, 0, 238, 0.2);
  transition: all 0.3s ease;
  padding: 0 24px !important;
  letter-spacing: 0.5px;
  text-transform: none;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(98, 0, 238, 0.3);
}

.submit-btn:active {
  transform: translateY(0);
}

/* Animations */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>